import WarningAmber from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../hooks/useNumberFormat';
import type {
  CampaignConcurrencyConflictPayload,
  CampaignConcurrencyConflictRow,
} from '../utils/campaignConcurrencyConflict';

interface CampaignConcurrencyConflictDialogProps {
  open: boolean;
  conflict: CampaignConcurrencyConflictPayload | null;
  onClose: () => void;
}

export default function CampaignConcurrencyConflictDialog({
  open,
  conflict,
  onClose,
}: CampaignConcurrencyConflictDialogProps) {
  const { t } = useTranslation(['pages', 'common']);
  const { formatNumber } = useNumberFormat();

  if (!conflict) {
    return null;
  }

  const metricItems = [
    {
      label: t('pages:campaigns.concurrencyConflict.limit'),
      value: conflict.limit,
    },
    {
      label: t('pages:campaigns.concurrencyConflict.activeConcurrency'),
      value: conflict.activeConcurrency,
    },
    {
      label: t('pages:campaigns.concurrencyConflict.requestedConcurrency'),
      value: conflict.requestedConcurrency,
    },
    {
      label: t('pages:campaigns.concurrencyConflict.projectedConcurrency'),
      value: conflict.projectedConcurrency,
    },
  ];
  const renderCampaignTypeLabel = (campaignType?: CampaignConcurrencyConflictRow['campaignType']) => {
    if (!campaignType) {
      return '-';
    }
    return t(`pages:campaigns.typeChipLabels.${campaignType}`);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="warning" />
          <Typography component="span" variant="h6">
            {t('pages:campaigns.concurrencyConflict.title')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning">
            {t('pages:campaigns.concurrencyConflict.summary', {
              campaign: conflict.campaign?.name || t('pages:campaigns.concurrencyConflict.thisCampaign'),
              excess: formatNumber(conflict.excessConcurrency),
            })}
          </Alert>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.5,
            }}
          >
            {metricItems.map((item) => (
              <Box
                key={item.label}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5 }}>
                  {formatNumber(item.value)}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography variant="body2" color="text.secondary">
            {t('pages:campaigns.concurrencyConflict.guidance')}
          </Typography>

          {conflict.activeCampaigns.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('pages:campaigns.concurrencyConflict.activeCampaigns')}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common:subscription.headerConcurrency.columns.campaignType')}</TableCell>
                    <TableCell>{t('common:subscription.headerConcurrency.columns.campaignName')}</TableCell>
                    <TableCell align="right">
                      {t('common:subscription.headerConcurrency.columns.concurrency')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conflict.activeCampaigns.map((campaign) => (
                    <TableRow key={campaign.id || campaign.name}>
                      <TableCell>{renderCampaignTypeLabel(campaign.campaignType)}</TableCell>
                      <TableCell>{campaign.name}</TableCell>
                      <TableCell align="right">{formatNumber(campaign.maxConcurrentCalls)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
